/**
 * Dev Tier Override — persisted tier simulation for testing paid features.
 *
 * Only active in __DEV__. Production reads are always null.
 * Stored in AsyncStorage so it survives app restarts.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '@/lib/logger';
import type { Tier } from '@/lib/subscription';

const log = createLogger('DEV_TIER');
const STORAGE_KEY = 'duel:dev_tier_override';

let cachedTier: Tier | null = null;
let initialized = false;

// Listeners for reactive updates
type Listener = (tier: Tier | null) => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) fn(cachedTier);
}

export const devTierOverride = {
  async init(): Promise<void> {
    if (initialized || !__DEV__) return;
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored && ['free', 'pro', 'coach_pro', 'org_pro'].includes(stored)) {
        cachedTier = stored as Tier;
      }
      initialized = true;
      log.info('Loaded dev tier override:', cachedTier ?? 'none');
    } catch {
      initialized = true;
    }
  },

  get(): Tier | null {
    if (!__DEV__) return null;
    return cachedTier;
  },

  async set(tier: Tier | null): Promise<void> {
    if (!__DEV__) return;
    cachedTier = tier;
    try {
      if (tier) {
        await AsyncStorage.setItem(STORAGE_KEY, tier);
      } else {
        await AsyncStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      log.warn('Failed to persist dev tier override');
    }
    notify();
    log.info('Dev tier override set to:', tier ?? 'none (real profile)');
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
};
